import {AssignedRequirement, Baseline} from 'types';
import {
	FieldsToCreatePathOfBaselineDetailsPage,
	createPathOfBaselineDetailsPage,
} from '../BaselineDetailsPage/BaselineDetailsPage.utils';
import {createNamespaceForVehicleProjects} from '../../VehicleProjects/hooks/vehicleProjectsTranslationHooks';
import {
	AssignedRequirementWithID,
	mapProjectRequirement,
} from 'features/VehicleProjects/AssignedEntities/AssignedRequirementColumns';
import {GetBaselineByIdWithAssignedReqsQuery} from '../hooks/baseline.generated';
import {alphabeticalSort} from 'helpers/sortHelpers';

export enum ComparisonResult {
	Unchanged = 'Unchanged',
	Changed = 'Changed',
	Deleted = 'Deleted',
	New = 'New',
}

export type ComparedAssignedRequirement = AssignedRequirementWithID & {
	comparisonResult: ComparisonResult;
};

export interface FieldsToCreatePathOfBaselineComparisonPage
	extends FieldsToCreatePathOfBaselineDetailsPage {
	idOfBaselineToCompareTo: Baseline['id'];
}

export const createPathOfBaselinesComparisonPage = ({
	idOfBaselineToCompareTo,
	...other
}: FieldsToCreatePathOfBaselineComparisonPage): string => {
	const pathOfDetailsPage: string = createPathOfBaselineDetailsPage(other);
	return `${pathOfDetailsPage}/compare/${idOfBaselineToCompareTo}`;
};

export const NAMESPACE_OF_BASELINE_CHANGES: string =
	createNamespaceForVehicleProjects('baselineChanges');

export const generateComparisonResult = (
	baselineData1: GetBaselineByIdWithAssignedReqsQuery | undefined,
	baselineData2: GetBaselineByIdWithAssignedReqsQuery | undefined,
) => {
	const assignedReq1 = (
		baselineData1?.baselineById?.assignedRequirements ?? []
	).map(x => mapProjectRequirement(x as AssignedRequirement));

	const assignedReq2 = (
		baselineData2?.baselineById?.assignedRequirements ?? []
	).map(x => mapProjectRequirement(x as AssignedRequirement));

	const map1 = new Map(assignedReq1.map(x => [x.requirementId ?? '', x]));
	const map2 = new Map(assignedReq2.map(x => [x.requirementId ?? '', x]));

	let comparisonResult = ComparisonResult.Unchanged;
	const assignedReqUnchanged = assignedReq1
		.filter(
			x =>
				map2.has(x.requirementId ?? '') &&
				map2.get(x.requirementId ?? '')?.id === x.id,
		)
		.map(x => ({...x, comparisonResult}));

	comparisonResult = ComparisonResult.Changed;
	const assignedReqChanged = assignedReq1
		.filter(
			x =>
				map2.has(x.requirementId ?? '') &&
				map2.get(x.requirementId ?? '')?.id !== x.id,
		)
		.map(x => ({...x, comparisonResult}));

	comparisonResult = ComparisonResult.Deleted;
	const assignedReqDeleted = assignedReq1
		.filter(x => !map2.has(x.requirementId ?? ''))
		.map(x => ({...x, comparisonResult}));

	comparisonResult = ComparisonResult.New;
	const assignedReqNew = assignedReq2
		.filter(x => !map1.has(x.requirementId ?? ''))
		.map(x => ({...x, comparisonResult}));

	const compiledResults = [
		...assignedReqUnchanged,
		...assignedReqChanged,
		...assignedReqDeleted,
		...assignedReqNew,
	] as ComparedAssignedRequirement[];

	return alphabeticalSort(compiledResults, 'requirementId');
};
